import { Box, IconButton, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { DesktopIcon, DeviceMobileIcon, GlobeIcon, PencilSimpleIcon } from '@phosphor-icons/react';

export type StudioDevice = 'desktop' | 'mobile';
// What's actually live for a public visitor right now, computed the same
// way public_read_published's RLS is (see src/lib/content-visibility.ts) —
// not a separate "preview as" simulation. 'draft' is the normal editable
// canvas; 'published' is a read-only rendering of exactly what's public,
// inline, so there's no separate page to navigate to just to check.
export type StudioViewState = 'draft' | 'published';

interface StudioTopBarProps {
  pageTitle: string;
  device: StudioDevice;
  onDeviceChange: (device: StudioDevice) => void;
  viewState: StudioViewState;
  onViewStateChange: (viewState: StudioViewState) => void;
}

// Lives inside the main viewing space (to the right of the Pages sidebar),
// not spanning the full app width — the platform's own MainNav already
// owns the top-level chrome, so this is scoped, in-content toolbar rather
// than a second page-wide AppBar. Deliberately minimal: device and state
// are the only controls here. Publishing is a page-by-page action (each
// page's own Published switch in Page Settings); unit-level settings live
// in the Hub, not here; Site Styles lives in the Pages sidebar's bottom
// icon row alongside Data/People/Settings/Health, not here.
export function StudioTopBar({
  pageTitle,
  device,
  onDeviceChange,
  viewState,
  onViewStateChange,
}: StudioTopBarProps) {
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'center',
        bgcolor: 'background.paper',
        gap: 2,
        px: 3,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {pageTitle}
        </Typography>
      </Box>

      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', ml: 'auto' }}>
        <Tooltip title="Desktop width">
          <IconButton color={device === 'desktop' ? 'primary' : 'default'} onClick={() => onDeviceChange('desktop')} aria-label="Desktop width">
            <DesktopIcon size={20} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Mobile width">
          <IconButton color={device === 'mobile' ? 'primary' : 'default'} onClick={() => onDeviceChange('mobile')} aria-label="Mobile width">
            <DeviceMobileIcon size={20} />
          </IconButton>
        </Tooltip>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewState}
          onChange={(_event, value: StudioViewState | null) => {
            if (value) onViewStateChange(value);
          }}
          aria-label="Draft or published view"
          sx={{ ml: 1 }}
        >
          <ToggleButton value="draft" aria-label="Edit draft">
            <Tooltip title="Edit draft">
              <PencilSimpleIcon size={16} />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="published" aria-label="View published">
            <Tooltip title="View what's actually published">
              <GlobeIcon size={16} />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    </Stack>
  );
}
